import type {IconName} from '@wandb/components';

import {activeRouteClaim, type RouteClaimTable} from '../routeClaims';
import type {RemoteName} from '../shellConfig';

export type ForgeProduct = {
  id: string;
  path?: string;
  label: string;
  description: string;
  icon: IconName;
  remote?: RemoteName;
};

/**
 * ARIA's product id, named because it doubles as its analytics `app_name`.
 * ARIA is not a configured remote — it is a second module exposed by the wandb
 * remote's container and mounted into the shell header — so it has no
 * `RemoteName` to be identified by, and this is the one place its name lives.
 */
export const ARIA_PRODUCT_ID = 'aria';

export const forgeProducts: readonly ForgeProduct[] = [
  {
    id: ARIA_PRODUCT_ID,
    label: 'ARIA',
    description: 'Coding agent for the AI loop',
    icon: 'lightboard',
  },
  {
    id: 'wandb',
    label: 'Weights & Biases',
    description: 'Track ML experiments and evaluate AI applications',
    icon: 'wandb-alt',
    remote: 'wandb',
  },
  {
    id: 'post-training',
    label: 'Post-Training',
    description: 'Distillation, RL and SFT',
    icon: 'dumbbell',
  },
  {
    id: 'agentLens',
    path: '/agent-lens',
    label: 'Agent Lens',
    description: 'Intelligent agent observability and improvement',
    icon: 'aperture',
    remote: 'agentLens',
  },
  {
    id: 'inference',
    label: 'Inference',
    description: 'Leverage the latest models',
    icon: 'inference',
  },
  {
    id: 'sandboxes',
    label: 'Sandboxes',
    description: 'On-demand, isolated CPU and GPU compute',
    icon: 'sandbox-playground',
  },
  {
    id: 'notebooks',
    label: 'Notebooks',
    description: 'Reactive Python notebooks',
    icon: 'notebook',
  },
  {
    id: 'registry',
    label: 'Registry',
    description: 'System of record and controls for assets',
    icon: 'registries',
  },
];

export function productForRemote(remote: RemoteName): ForgeProduct {
  return (
    forgeProducts.find(product =>
      remote === 'distillation'
        ? product.id === 'post-training'
        : product.remote === remote
    ) ?? forgeProducts[0]
  );
}

/**
 * The product a shell path belongs to. A claimed path resolves to the product
 * named by the route claim when there is one — `/notebooks` highlights Notebooks even
 * though the W&B remote renders it — and otherwise to the claiming remote's own
 * product.
 */
export function currentProductForPath(
  pathname: string,
  table: RouteClaimTable
): ForgeProduct | undefined {
  const active = activeRouteClaim(table, pathname);
  if (active) {
    return (
      forgeProducts.find(
        product =>
          (product.path ?? `/${product.id}`) === active.routeClaim.shellPath
      ) ?? productForRemote(active.remote.name)
    );
  }
  return forgeProducts.find(
    product =>
      !product.remote &&
      (pathname === (product.path ?? `/${product.id}`) ||
        pathname.startsWith(`${product.path ?? `/${product.id}`}/`))
  );
}
